import { sleep } from '../shared/sleep'
import { warnAboutEvent } from './checks'
import { createClient } from './client'
import type { Client } from './client'
import { buildEvent } from './event'
import { defaultHost, findSkipReason, isOptedOut } from './guards'
import { createCallQueue } from './queue'
import type {
  EventInput,
  IngestClient,
  LoomductConfig,
  LoomductError,
  LoomductEvent,
  Plugin,
  PluginContext,
  PluginMap,
  Properties,
  PropertyValue,
} from './types'

const idleClient: IngestClient = {
  request: () => Promise.resolve(undefined),
}

export class Loomduct<const P extends readonly Plugin[] = readonly Plugin[]> {
  readonly plugins = {} as PluginMap<P>
  declare readonly load: () => Promise<void>
  declare readonly track: (name: string, properties?: Properties) => void
  declare readonly identify: (
    userId: string,
    properties?: Record<string, PropertyValue | null | undefined>,
  ) => Promise<void>
  declare readonly reset: () => Promise<void>
  declare readonly use: <T extends Plugin>(plugin: T) => T
  declare readonly beforeSend: (
    hook: ((event: LoomductEvent) => LoomductEvent | null) | null,
  ) => void
  declare readonly onError: (
    hook: ((error: LoomductError) => void) | null,
  ) => void
  declare readonly getVisitorId: () => string | undefined
  declare readonly destroy: () => void

  constructor(config: LoomductConfig<P>) {
    const configuredHost = config.host ?? defaultHost
    const host = configuredHost.endsWith('/')
      ? configuredHost.slice(0, -1)
      : configuredHost
    const queue = createCallQueue()
    const plugins: Plugin[] = []
    const broken = new Set<Plugin>()
    let beforeSendHook = config.beforeSend
    let errorHook = config.onError
    let client: Client | undefined
    let starting: Promise<void> | undefined
    let loaded = false
    let destroyed = false
    let pageviewSent = false
    let lastIdentify: string | undefined
    let debug = config.debug ?? false
    try {
      if (process.env.NODE_ENV !== 'production') {
        debug = config.debug ?? true
      }
    } catch {}

    const log = (...args: unknown[]) => {
      if (debug) {
        console.info('[loomduct]', ...args)
      }
    }

    const reportError = (error: LoomductError) => {
      log(error.code, error.message)
      try {
        errorHook?.(error)
      } catch {}
    }

    const fail = (plugin: Plugin, error: unknown) => {
      broken.add(plugin)
      reportError({
        code: 'plugin',
        message: `${plugin.name}: ${String(error)}`,
      })
    }

    const run = (
      plugin: Plugin,
      hook: (plugin: Plugin) => unknown,
    ): unknown => {
      if (!broken.has(plugin)) {
        try {
          return hook(plugin)
        } catch (error) {
          fail(plugin, error)
        }
      }
      return undefined
    }

    const loadPlugin = async (plugin: Plugin) => {
      const loading = run(plugin, (item) => item.load?.())
      if (loading instanceof Promise) {
        await Promise.race([
          loading.catch((error: unknown) => {
            fail(plugin, error)
          }),
          sleep(2000),
        ])
      }
    }

    const send = (input: EventInput, options?: { beacon?: boolean }): void => {
      if (destroyed) {
        return
      }
      if (!client) {
        queue.push('call', () => {
          send(input, options)
        })
        return
      }
      if (isOptedOut()) {
        return
      }
      const referrer =
        input.referrer ?? (pageviewSent ? undefined : document.referrer)
      pageviewSent ||= input.name === '$pageview' || input.name === '$not_found'
      let event: LoomductEvent | null = null
      try {
        event = buildEvent(input, referrer, config)
      } catch (error) {
        reportError({ code: 'invalid_event', message: String(error) })
        return
      }
      for (const plugin of plugins) {
        if (plugin.beforeSend && !broken.has(plugin)) {
          try {
            event = plugin.beforeSend(event)
          } catch (error) {
            fail(plugin, error)
            return
          }
          if (!event) {
            return
          }
        }
      }
      if (beforeSendHook) {
        const ready = event
        try {
          event = beforeSendHook(ready)
        } catch (error) {
          reportError({
            code: 'before_send',
            message: String(error),
            event: ready,
          })
          return
        }
        if (!event) {
          return
        }
      }
      log(event.name, event.url)
      void client.request({
        path: '/l/e',
        body: event,
        kind: 'event',
        beacon: options?.beacon,
      })
    }

    const getVisitorId = () => client?.getVisitorId()

    const context: PluginContext = {
      config,
      send,
      get client() {
        return client ?? idleClient
      },
      reportError,
      getVisitorId,
    }

    const add = (plugin: Plugin) => {
      const index = plugins.findIndex((item) => item.name === plugin.name)
      const previous = plugins[index]
      if (previous) {
        try {
          if (process.env.NODE_ENV !== 'production') {
            console.warn(`[loomduct] plugin ${plugin.name} was added twice`)
          }
        } catch {}
        run(previous, (item) => item.destroy?.())
        plugins.splice(index, 1)
      }
      plugins.push(plugin)
      ;(this.plugins as Record<string, Plugin>)[plugin.name] = plugin
      run(plugin, (item) => item.init?.(context))
    }

    const start = async () => {
      if (typeof window === 'undefined' || destroyed) {
        return
      }
      const reason = findSkipReason(config)
      if (reason) {
        if (reason === 'token') {
          console.warn('[loomduct] missing token')
        }
        log('skipped:', reason)
        return
      }
      client = createClient({ token: config.token, host, reportError, log })
      for (const plugin of plugins.slice()) {
        await loadPlugin(plugin)
        if (destroyed) {
          return
        }
      }
      loaded = true
      queue.drain()
    }

    const identify = (
      userId: string,
      properties?: Record<string, PropertyValue | null | undefined>,
    ): Promise<void> => {
      try {
        if (process.env.NODE_ENV !== 'production' && !userId) {
          console.warn('[loomduct] identify() needs a user id')
        }
      } catch {}
      const current = client
      if (!loaded || !current) {
        queue.push('identify', () => {
          void identify(userId, properties)
        })
        return Promise.resolve()
      }
      const key = JSON.stringify([userId, properties ?? {}])
      if (destroyed || isOptedOut() || key === lastIdentify) {
        return Promise.resolve()
      }
      lastIdentify = key
      return current
        .request({
          path: '/l/u',
          body: { properties: { ...properties, userId } },
          kind: 'identity',
        })
        .then((data) => {
          if (data === undefined && lastIdentify === key) {
            lastIdentify = undefined
          }
        })
    }

    const reset = (): Promise<void> => {
      const current = client
      if (!loaded || !current) {
        queue.push('reset', () => {
          void reset()
        })
        return Promise.resolve()
      }
      if (destroyed) {
        return Promise.resolve()
      }
      for (const plugin of plugins) {
        run(plugin, (item) => item.flush?.())
      }
      lastIdentify = undefined
      return current.reset()
    }

    for (const plugin of config.plugins ?? []) {
      add(plugin)
    }
    Object.defineProperty(this, Symbol.for('loomduct.instance'), {
      value: true,
    })
    Object.assign(this, {
      load: () =>
        (starting ??= start().catch((error: unknown) => {
          reportError({ code: 'plugin', message: String(error) })
        })),
      track: (name: string, properties?: Properties) => {
        try {
          if (process.env.NODE_ENV !== 'production') {
            warnAboutEvent(name, properties)
          }
        } catch {}
        if (loaded) {
          send({ name, properties })
        } else {
          queue.push('call', () => {
            send({ name, properties })
          })
        }
      },
      identify,
      reset,
      use: <T extends Plugin>(plugin: T): T => {
        if (!destroyed) {
          add(plugin)
          if (client) {
            void loadPlugin(plugin)
          }
        }
        return plugin
      },
      beforeSend: (
        hook: ((event: LoomductEvent) => LoomductEvent | null) | null,
      ) => {
        beforeSendHook = hook ?? undefined
      },
      onError: (hook: ((error: LoomductError) => void) | null) => {
        errorHook = hook ?? undefined
      },
      getVisitorId,
      destroy: () => {
        if (!destroyed) {
          destroyed = true
          for (const plugin of plugins) {
            run(plugin, (item) => item.destroy?.())
          }
          client?.close()
        }
      },
    })
  }
}
